import { useCallback, useEffect, useRef, useState } from 'react';

type UseModalAnimationOptions = {
  isOpen: boolean;
  onClose: () => void;
  durationMs?: number;
};

const OPEN_DELAY_MS = 10;

const useModalAnimation = ({ isOpen, onClose, durationMs = 300 }: UseModalAnimationOptions) => {
  const [animate, setAnimate] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const openTimerRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleClose = useCallback(() => {
    setAnimate(false);
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => {
      onClose();
    }, durationMs);
  }, [onClose, durationMs]);

  useEffect(() => {
    clearTimers();
    if (isOpen) {
      openTimerRef.current = window.setTimeout(() => setAnimate(true), OPEN_DELAY_MS);
    } else {
      setAnimate(false);
    }

    return () => {
      clearTimers();
    };
  }, [isOpen]);

  return { animate, handleClose };
};

export default useModalAnimation;
